import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import Markdown from "./Markdown";
import { Controller, useForm } from "react-hook-form";
import MarkdownEditor from "./MarkdownEditor";
import FileUploadPlugin from "./MarkdownEditor/plugins/FileUpload";
import SuggestionPlugin from "./MarkdownEditor/plugins/Suggestion";
import { ErrorMessage } from "./FormGroup";
import { Button } from "@/kit/ui/button";
import { Edit03Icon } from "@/icons";

interface SubmitData {
  content: string;
}

interface Props {
  id: ID;
  onSubmit: (data: SubmitData) => Promise<void>;
  canUploadFiles?: boolean;
  canUseSuggestions?: boolean;
  className?: string;
  title?: React.ReactNode;
  titleEnd?: React.ReactNode;
  fallback?: React.ReactNode;
  canEdit?: boolean;
  disabled?: boolean;
  content?: string;
  maxLength?: number;
}

type FormData = {
  value: string;
};

export default function EditMarkdownSection({
  id,
  title,
  titleEnd,
  onSubmit,
  fallback,
  content = "",
  canEdit = false,
  disabled = false,
  canUploadFiles = false,
  canUseSuggestions = true,
  maxLength,
}: Props) {
  const {
    handleSubmit,
    reset,
    control,
    formState: { dirtyFields },
  } = useForm<FormData>();
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);

  const resetForm = () => {
    reset();
    setFormError(undefined);
    setIsEditing(false);
  };

  const onUpdate = handleSubmit((data) => {
    if (disabled) {
      return;
    }
    if (data.value === content) {
      setIsEditing(false);
      return;
    }
    if (content && !dirtyFields.value) {
      return;
    }

    onSubmit({ content: data.value.trim() })
      .then(resetForm)
      .catch((err) => setFormError(String(err)));
  });

  const nonEditingContent = content ? <Markdown>{content}</Markdown> : fallback;
  return (
    <form onSubmit={onUpdate}>
      <div className="flex flex-row justify-between items-center gap-3">
        {title && <div className="grow">{title}</div>}
        {titleEnd}
        {canEdit &&
          (isEditing ? (
            <div className="flex flex-row gap-3">
              <Button
                variant="secondary"
                onClick={() => setIsEditing(false)}
                disabled={disabled}
              >
                <FormattedMessage defaultMessage="Cancel" />
              </Button>
              <Button variant="primary" disabled={disabled} onClick={onUpdate}>
                <FormattedMessage defaultMessage="Save" />
              </Button>
            </div>
          ) : (
            <div>
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                <Edit03Icon className="w-5 h-5" />
                <FormattedMessage defaultMessage="Edit" />
              </Button>
            </div>
          ))}
      </div>
      {formError && <ErrorMessage error={formError} />}
      <div className="pb-16 pt-4">
        {isEditing ? (
          <Controller
            name="value"
            control={control}
            rules={{ maxLength }}
            defaultValue={content}
            render={({ field }) => (
              <MarkdownEditor {...field} namespaceId={id}>
                {canUploadFiles && <FileUploadPlugin />}
                {canUseSuggestions && (
                  <SuggestionPlugin source={{ member: id }} />
                )}
              </MarkdownEditor>
            )}
          />
        ) : (
          nonEditingContent
        )}
      </div>
    </form>
  );
}
